import React, { FunctionComponent, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Button, Form, InfoIcon, Layout, Modal, PageWrap, Spinner, Table } from '@vwfs-bronson/bronson-react';
import { Formik } from 'formik';
import { NumericFormField } from '../../components/FormFields/NumericFormField';
import { departmentList } from '../../resources/base/models/departmentList';
import { FuelType } from '../../resources/base/models/vehicleTypes';
import { calculateQuote } from '../../services/api/miles/miles-services';
import { formatDecimalComma, transformDateFormatWithSlash } from '../../services/common/format';
import { store } from '../../services/redux';
import { btoConfigSelectors } from '../../services/redux/features/btoConfig.slice';
import { loadingActions, loadingSelectors } from '../../services/redux/features/loading.slice';
import { quoteSelectors } from '../../services/redux/features/quote.slice';
import { useNavigate } from '../../services/routing/navigate';
import { totalCost } from '../utils';
import { calculateRegistrationCosts, getTotalRegistrationCost } from './registrationCostsUtils';
import { RegistrationCostPopupModalSchema } from './validationSchema';
import { WarningModal } from './WarningModal';

interface RegistrationCostModalProps {
  visible: boolean;
  onCancel: (val?) => void;
  onConfirm: (registrationCostsTotal: number) => void;
  department?: string;
  includingRegistration: boolean;
  fuelType?: string;
  resetPopupValues: boolean;
  setResetPopupValues: (val) => void;
  updateFiscalPower: (quote: QuoteSchema.Schemas.QuoteResponse) => void;
  fiscalPower?: number;
  estDelivery?: string;
  catalogPrice?: number;
  quoteId?: string;
  prefillData?: QuoteSchema.Schemas.QuoteResponse;
  isCarUsedOrDemo?: boolean;
  isFormReadonly?: boolean;
}

type InitialValuesType = {
  malusBeforeTax: number;
  lcvExtraBeforeTax: number;
  inputWeight: number;
  weightTax: number | null;
  autonomyValue: number;
  grossAmountRegionalTax: number;
  grossAmountManagementAndDelivery: number;
};

export const RegistrationCostModal: FunctionComponent<RegistrationCostModalProps> = (props) => {
  const formRef: any = useRef();
  const { t } = useTranslation();
  const {
    visible,
    onCancel,
    onConfirm,
    department,
    includingRegistration,
    fuelType,
    resetPopupValues,
    setResetPopupValues,
    updateFiscalPower,
    fiscalPower,
    estDelivery,
    catalogPrice,
    quoteId,
    isCarUsedOrDemo,
    isFormReadonly,
  } = props;
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  const onSubmit = () => {};

  const MALUS_AND_WEIGHT_TAX_BOUNDARY = 80000;
  const isTotalTaxOutOfBoundary = (weightTax: number, malusBeforeTax: number) => {
    const totalTax: number = Number(weightTax || 0) + Number(malusBeforeTax || 0);

    return totalTax > MALUS_AND_WEIGHT_TAX_BOUNDARY;
  };

  const quoteData = useSelector(quoteSelectors.selectQuote);
  const isBtoConfigLocked = useSelector(btoConfigSelectors.selectBtoConfigLocked);
  const [showWarningPopup, setShowWarningPopup] = useState<boolean>(false);
  const [confirmationDisabled, setConfirmationDisabled] = useState<boolean>(true);
  const [isPrefillInitialLoad, setIsPrefillInitialLoad] = useState<boolean>(!!quoteData);
  const [calculationDone, setCalculationDone] = useState(false);
  const calculatedValuesRef = useRef<
    Omit<InitialValuesType, 'grossAmountRegionalTax' | 'grossAmountManagementAndDelivery'>
  >({ malusBeforeTax: 0, lcvExtraBeforeTax: 0, inputWeight: 0, weightTax: 0, autonomyValue: 0 });
  const [registrationCosts, setRegistrationCosts] = useState<number>(0);
  const navigate = useNavigate();
  const isLoading = useSelector(loadingSelectors.selectLoading);
  const isQuoteEditable = useSelector(quoteSelectors.selectIsQuoteEditable);

  const initialValues: InitialValuesType = useMemo(() => {
    const values = {
      malusBeforeTax: 0,
      lcvExtraBeforeTax: 0,
      inputWeight: 0,
      weightTax: 0,
      autonomyValue: 0,
      grossAmountRegionalTax: 0,
      grossAmountManagementAndDelivery: 0,
    };

    if (quoteData) {
      const registrationCosts = calculateRegistrationCosts(quoteData);
      values.malusBeforeTax = registrationCosts.malusBeforeTax;
      values.lcvExtraBeforeTax = registrationCosts.lcvExtraBeforeTax;
      values.weightTax = registrationCosts.weightTax;
      values.inputWeight = quoteData.vehicleData?.inputWeight || 0;
      values.autonomyValue = quoteData.vehicleData?.autonomy?.value || 0;
    }

    if (formRef.current) {
      formRef.current.setValues(values);
    }
    return values;
  }, [quoteData]);

  const [grossAmountRegionalTax, setGrossAmountRegionalTax] = useState<number>(initialValues.grossAmountRegionalTax);
  const [grossAmountManagementAndDelivery, setGrossAmountManagementAndDelivery] = useState<number>(
    initialValues.grossAmountManagementAndDelivery
  );

  // Initialize prefill data if available
  if (quoteData) {
    const registrationCosts = calculateRegistrationCosts(quoteData);
    initialValues.malusBeforeTax = registrationCosts.malusBeforeTax;
    initialValues.lcvExtraBeforeTax = registrationCosts.lcvExtraBeforeTax;
    initialValues.weightTax = registrationCosts.weightTax;
    initialValues.inputWeight = quoteData.vehicleData?.inputWeight || 0;
    initialValues.autonomyValue = quoteData.vehicleData?.autonomy?.value || 0;
  }

  const updateFormAfterCalculation = (
    response: QuoteSchema.Schemas.QuoteResponse,
    preventConfirmation: boolean = false
  ) => {
    const registrationCosts = calculateRegistrationCosts(response);
    formRef.current.setFieldValue('weightTax', response.vehicleData?.weightTax || 0);
    formRef.current.setFieldValue('inputWeight', response.vehicleData?.inputWeight || 0);
    formRef.current.setFieldValue('autonomyValue', response.vehicleData?.autonomy?.value || 0);
    formRef.current.setFieldValue('malusBeforeTax', registrationCosts.malusBeforeTax);
    formRef.current.setFieldValue('lcvExtraBeforeTax', registrationCosts.lcvExtraBeforeTax);

    setGrossAmountRegionalTax(registrationCosts.regionalTaxAmount);
    setGrossAmountManagementAndDelivery(registrationCosts.managementAndDeliveryAmount);

    if (updateFiscalPower && 'financialProduct' in response) {
      updateFiscalPower(response);
    }

    setRegistrationCosts(getTotalRegistrationCost(registrationCosts));

    if (!preventConfirmation) {
      onConfirm(getTotalRegistrationCost(registrationCosts));
    }

    calculatedValuesRef.current = {
      autonomyValue: response.vehicleData?.autonomy?.value || 0,
      weightTax: response.vehicleData?.weightTax || 0,
      inputWeight: response.vehicleData?.inputWeight || 0,
      malusBeforeTax: registrationCosts.malusBeforeTax,
      lcvExtraBeforeTax: registrationCosts.lcvExtraBeforeTax,
    };
  };

  const calculateRegistrationCostsFromAPI = async () => {
    store.dispatch(loadingActions.setLoading(true));

    try {
      const request: QuoteSchema.Schemas.CalculateSalesQuoteRequest = {
        financialProduct: {
          ...(estDelivery && {
            contracts: [
              {
                deliveryData: {
                  deliveryDate: transformDateFormatWithSlash(estDelivery),
                },
              },
            ],
          }),
          offers: [
            {
              calculation: {
                registrationDepartment: department as QuoteSchema.Schemas.RegistrationDepartmentType,
                includingRegistration,
              },
            },
          ],
        },
        vehicleData: {
          ...(fiscalPower && { fiscalPower }),
          ...(catalogPrice && {
            milesDealerVehiclePrice: {
              grossAmount: catalogPrice,
            },
          }),
        },
      };
      if (!quoteId) {
        throw new Error('Missing quote id!');
      }

      const response = await calculateQuote(quoteId, request);
      updateFormAfterCalculation(response);
    } catch {
      navigate.error();
    } finally {
      store.dispatch(loadingActions.setLoading(false));
    }
  };

  useEffect(() => {
    if (!isQuoteEditable) return;
    if (!fiscalPower || !department || !includingRegistration || isBtoConfigLocked) {
      return;
    }

    if (isPrefillInitialLoad) {
      setIsPrefillInitialLoad(false);
      return;
    }
    if (!departmentList.some((dep) => dep.value === department)) return;

    calculateRegistrationCostsFromAPI();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fiscalPower, department, includingRegistration, isPrefillInitialLoad]);

  // eslint-disable-next-line react-hooks/rules-of-hooks
  useEffect(() => {
    if (resetPopupValues) {
      formRef.current?.resetForm({ values: initialValues });
      setResetPopupValues(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetPopupValues]);

  const calculateAndSendDeliveryCosts = async () => {
    const currentValues = formRef.current.values;

    const request: QuoteSchema.Schemas.CalculateSalesQuoteRequest = {
      vehicleData: {
        ...(fiscalPower !== undefined && {
          fiscalPower,
        }),
        weightTax: currentValues?.weightTax === 0 || currentValues?.inputWeight !== 0 ? null : currentValues?.weightTax,
        inputWeight: currentValues?.inputWeight || 0,
        co2TaxAmount: currentValues?.malusBeforeTax || 0,
        autonomy: {
          value: currentValues?.autonomyValue || 0,
          unit: 'km',
        },
      },
      financialProduct: {
        offers: [
          {
            deliveryOptions: [
              {
                deliveryCostComponents: [
                  {
                    isSelected: true,
                    costType: 'REGISTRATION',
                    deliveryCostAmount: {
                      taxCode: 'ParafiscalTax',
                      netAmount: currentValues?.lcvExtraBeforeTax,
                    },
                  },
                ],
              },
            ],
          },
        ],
      },
    };
    try {
      store.dispatch(loadingActions.setLoading(true));
      if (!quoteId) {
        throw new Error('Missing quote id!');
      }
      const response = await calculateQuote(quoteId, request);
      updateFormAfterCalculation(response, true);
      setCalculationDone(true);
    } catch {
      navigate.error();
    } finally {
      store.dispatch(loadingActions.setLoading(false));
    }
  };

  return (
    <>
      <Modal
        id="registrationCostModal"
        stretch
        title={t('registrationCostModal:title')}
        buttonConfirmText={t('registrationCostModal:apply')}
        buttonCancelText={t('registrationCostModal:cancel')}
        buttonConfirmType="submit"
        shown={visible}
        confirmationDisabled={confirmationDisabled || !calculationDone}
        onClose={() => onCancel('close')}
        onCancel={() => {
          onCancel('close');
          formRef.current?.resetForm({ values: initialValues });
        }}
        onConfirm={() => {
          onConfirm(registrationCosts);
        }}
        asPortal
        className="c-modal--large"
      >
        <Formik
          initialValues={initialValues}
          innerRef={formRef}
          enableReinitialize
          onSubmit={() => onSubmit()}
          validationSchema={RegistrationCostPopupModalSchema(fuelType)}
        >
          {(formProps) => {
            const { values, handleSubmit, errors, setFieldValue, dirty } = formProps;
            const handleSubmitOwn = (e) => {
              handleSubmit(e);
            };

            const didFieldsChange = () => {
              if (!calculatedValuesRef.current) return true;

              return (
                values.autonomyValue !== calculatedValuesRef.current.autonomyValue ||
                values.malusBeforeTax !== calculatedValuesRef.current.malusBeforeTax ||
                values.weightTax !== calculatedValuesRef.current.weightTax ||
                values.lcvExtraBeforeTax !== calculatedValuesRef.current.lcvExtraBeforeTax ||
                values.inputWeight !== calculatedValuesRef.current.inputWeight
              );
            };

            // eslint-disable-next-line react-hooks/rules-of-hooks
            useEffect(() => {
              const weightTax: number = Number(values?.weightTax || 0);
              const malusBeforeTax: number = Number(values?.malusBeforeTax || 0);

              const shouldShowWarningPopup: boolean = isTotalTaxOutOfBoundary(weightTax, malusBeforeTax);
              setShowWarningPopup(shouldShowWarningPopup);

              const isValid: boolean = !!formRef?.current?.isValid && Object.keys(errors).length === 0;
              const shouldDisableConfirmation: boolean = shouldShowWarningPopup || !isValid;
              setConfirmationDisabled(shouldDisableConfirmation);
              if (didFieldsChange()) {
                setCalculationDone(false);
              }

              // eslint-disable-next-line react-hooks/exhaustive-deps
            }, [errors, values]);
            const bulletPoint = '\u2022';

            return (
              <Form onSubmit={handleSubmitOwn} className="u-mb-large">
                <PageWrap>
                  <Layout>
                    <Table>
                      <Table.Thead>
                        <Table.Tr>
                          <Table.Th />
                          <Table.Th>{t('registrationCostModal:tva')}</Table.Th>
                          <Table.Th>{t('registrationCostModal:priceAfterTax')}</Table.Th>
                        </Table.Tr>
                      </Table.Thead>
                      <Table.Tbody>
                        <Table.Tr>
                          <Table.Td>{t('registrationCostModal:carteGriseBasicPrice')}</Table.Td>
                          <Table.Td>{t('registrationCostModal:zeroPercentage')}</Table.Td>
                          <Table.Td className="u-text-right">
                            {formatDecimalComma(grossAmountRegionalTax) || 0} €
                          </Table.Td>
                        </Table.Tr>

                        <Table.Tr>
                          <Table.Td>{t('registrationCostModal:malusAmount')}</Table.Td>
                          <Table.Td>{t('registrationCostModal:zeroPercentage')}</Table.Td>
                          <Table.Td className="u-text-right">
                            <NumericFormField
                              fieldName="malusBeforeTax"
                              isDisabled={isCarUsedOrDemo || isFormReadonly}
                              type="money"
                              maximumFractionDigits={2}
                            />
                          </Table.Td>
                        </Table.Tr>

                        <Table.Tr>
                          <Table.Td>{t('registrationCostModal:lcvExtraTax')}</Table.Td>
                          <Table.Td>{t('registrationCostModal:zeroPercentage')}</Table.Td>
                          <Table.Td className="u-text-right">
                            <NumericFormField
                              fieldName="lcvExtraBeforeTax"
                              isDisabled={isCarUsedOrDemo || isFormReadonly}
                              type="money"
                              maximumFractionDigits={2}
                            />
                          </Table.Td>
                        </Table.Tr>

                        <Table.Tr>
                          <Table.Td data-vertical-align="top">
                            <Layout.Item className="u-p-none u-pt-small">
                              {t('registrationCostModal:weightTax')}
                              <InfoIcon aria-label="Information" icon="semantic-info">
                                <p className="u-mb-none">{t('registrationCostModal:tooltip:description')}</p>
                                <p className="u-ml-small u-mb-none">
                                  {`${bulletPoint} ${t('registrationCostModal:tooltip:directAmount')}`}
                                </p>
                                <p className="u-ml-small u-mb-none">{` ${t('registrationCostModal:tooltip:or')}`}</p>
                                <p className="u-ml-small u-mb-none">
                                  {`${bulletPoint} ${t('registrationCostModal:tooltip:indicateWeight')}`}
                                </p>
                              </InfoIcon>
                            </Layout.Item>
                          </Table.Td>
                          <Table.Td data-vertical-align="top">
                            <Layout.Item className="u-p-none u-pt-small">
                              {t('registrationCostModal:zeroPercentage')}
                            </Layout.Item>
                          </Table.Td>
                          <Table.Td className="u-text-right">
                            <Layout.Item className="u-p-none u-pt-xsmall">
                              <NumericFormField
                                fieldName="inputWeight"
                                labelTranslationKey={t('registrationCostModal:massInWorkingOrder')}
                                type="weight"
                                maximumFractionDigits={0}
                                isDisabled={isCarUsedOrDemo || isFormReadonly}
                                onValueChange={() => {
                                  setFieldValue('weightTax', 0);
                                }}
                              />
                            </Layout.Item>
                            <Layout.Item className="u-p-none u-pt-xsmall">
                              <NumericFormField
                                fieldName="autonomyValue"
                                labelTranslationKey={t('registrationCostModal:autonomy')}
                                type="km"
                                optional={fuelType !== FuelType.Hybrid}
                                maximumFractionDigits={0}
                                isDisabled={isCarUsedOrDemo || isFormReadonly}
                                onValueChange={() => {
                                  setFieldValue('weightTax', 0);
                                }}
                              />
                            </Layout.Item>
                            <Layout.Item className="u-p-none u-pt-small">
                              <Button action type="button" onClick={calculateAndSendDeliveryCosts} disabled={!dirty}>
                                {t('step1:configVehicle:calculateButton')}
                              </Button>
                            </Layout.Item>
                            <Layout.Item className="u-p-none">
                              <NumericFormField
                                labelTranslationKey={t('registrationCostModal:calculatedTax')}
                                fieldName="weightTax"
                                type="money"
                                maximumFractionDigits={2}
                                isDisabled={isCarUsedOrDemo || isFormReadonly}
                                onValueChange={() => {
                                  setFieldValue('inputWeight', 0);
                                  setFieldValue('autonomyValue', 0);
                                }}
                              />
                            </Layout.Item>
                          </Table.Td>
                        </Table.Tr>

                        <Table.Tr>
                          <Table.Td>{t('registrationCostModal:regManageFees')}</Table.Td>
                          <Table.Td>{t('registrationCostModal:zeroPercentage')}</Table.Td>
                          <Table.Td className="u-text-right">
                            {formatDecimalComma(grossAmountManagementAndDelivery) || 0} €
                          </Table.Td>
                        </Table.Tr>

                        <Table.Tr>
                          <Table.Td>{t('registrationCostModal:total')}</Table.Td>
                          <Table.Td />
                          <Table.Td className="u-text-right">
                            {formatDecimalComma(
                              totalCost(
                                values.malusBeforeTax,
                                values.lcvExtraBeforeTax,
                                grossAmountManagementAndDelivery,
                                grossAmountRegionalTax,
                                values?.weightTax || 0
                              )
                            )}{' '}
                            €
                          </Table.Td>
                        </Table.Tr>
                      </Table.Tbody>
                    </Table>
                  </Layout>
                </PageWrap>
              </Form>
            );
          }}
        </Formik>
        {/* TODO Remove when Bronson fixes BRONSD-1042 */}
        {isLoading && <Spinner fullPage />}
      </Modal>
      <WarningModal
        visible={showWarningPopup}
        onCancel={() => {
          setShowWarningPopup(false);
        }}
      />
    </>
  );
};
